/* eslint-disable complexity */
import { TextCollectionAttachment } from '@scale/shared/types/params/textcollection';
import { Alert, Button, Switch } from 'design-system';
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { TextInput, CircularProgress } from 'react-scale/components/design-system';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { useChatbotContext } from 'react-scale/dashboard.scale.com/contexts/ChatbotRankingContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ChatbotIcon from 'react-scale/components/scalers/tasks/textcollection/ChatbotRanking/ChatbotIcon';
import cx from 'classnames';
import API from '@scale/frontend-lib/dashboard-api';
import SanitizedMarkdown from 'react-scale/components/design-system/SanitizedMarkdown';
import _ from 'lodash';
import { useLocalStorage } from 'react-use-v13';
import uuid from 'uuid';
import {
  DISABLE_PASTING_ERROR_TEXT,
  AUTOTYPER_DETECT_STEP,
  AUTOTYPER_DETECT_LENGTH,
  AUTOTYPER_DETECT_MAX_INTERVAL_MS,
} from 'react-scale/components/scalers/tasks/textcollection/CustomTextField';
import { AllowedRapidAttachmentManagerMetadata } from '@scale/shared/defaults';
import { IRlhfPromptValidatorConfig } from '@scale/shared/types/rlhf';
import produce from 'immer';
import { compareTwoStrings } from 'string-similarity';
import debounce from 'lodash.debounce';
import { TextCollectionAnnotationsOrCallback } from '@scale/shared/types/textCollection';
import { Breadcrumb } from 'react-scale/components/scalers/tasks/textcollection/breadcrumbs';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

const CONTENTEDITABLE_ATTRIBUTE_NAME = 'contenteditable';
const DATA_GRAMM_ATTRIBUTE_NAME = 'data-gramm';
const MOUNT_SAPLING_AFTER_X_MS_NO_TYPING = 1500;
const MIN_WORDS_TO_MOUNT_SAPLING = 25;

interface Props {
  attachment: TextCollectionAttachment;
  projectId?: string;
  assignmentId?: string;
  subtaskMetadata?: any;
  prefillPrompt?: string;
  prefillRefText?: string;
  setAnnotations?: (
    annotations: TextCollectionAnnotationsOrCallback,
    breadcrumbs?: Breadcrumb[],
  ) => void;
  canStopStreaming?: boolean;
}
interface RowProps {
  isBot?: boolean;
  isAttachment?: boolean;
  text?: string;
  options?: string[];
  isPerConvo?: boolean;
}

const ChatBotViewV2: React.FC<Props> = (props: Props) => {
  const npovRewriteOpen = useUnleashFeatureFlag(
    'npov-rewrite-open',
    { projectId: props.projectId },
    false,
  );
  const styles = useChatBotViewStyles();

  const chatbotContext = useChatbotContext();
  const [submitting, setSubmitting] = useState(false);
  const [spellingAndGrammarCheckEnabled, setSpellingAndGrammarCheckEnabled] = useState(false);
  const [disablePasting, setDisablePasting] = useState(false);
  const [keystrokeIntervals, setKeystrokeIntervals] = React.useState<number[]>([]);
  const [lastKeystrokeTimestamp, setLastKeystrokeTimestamp] = React.useState<number | undefined>(
    undefined,
  );
  const [Sapling, setSapling] = useState<any>(null);
  const [validatorConfig, setValidatorConfig] = useState<IRlhfPromptValidatorConfig | undefined>(
    undefined,
  );
  const chatBoxTexts = chatbotContext.chatBoxTexts;
  const assignmentId = props.assignmentId || uuid.v4();
  const [submissionError, setSubmissionError] = useState<string | undefined>(undefined);
  const isRangedMultiTurn = chatbotContext.isRangedMultiTurn;

  const basePromptTexts = _.range(chatBoxTexts.length).map(i => {
    if (i === 0 && props.prefillPrompt !== undefined) {
      return props.prefillPrompt;
    }
    if (i === 1 && props.prefillRefText !== undefined) {
      return props.prefillRefText;
    }
    return '';
  });

  const [promptTexts, setPromptTexts] = useLocalStorage(
    'promptTexts' + assignmentId,
    basePromptTexts,
  );
  const [promptErrors, setPromptErrors] = useState<string[][]>(
    chatbotContext.chatBoxTexts.map(() => []),
  );
  const [promptWarnings, setPromptWarnings] = useState<string[]>([]);
  const [promptWithWarningOnly, setPromptWithWarningOnly] = useState<string | undefined>(undefined);
  const [useMarkdownPreview, setUseMarkdownPreview] = useState(false);

  const answeringForm = chatbotContext.fieldsToShow.length > 0;
  const transcriptRef = useRef<HTMLDivElement>(null);

  const validatePrompt = async () => {
    let validPrompt = true;
    let hasWarnings = false;
    if (!props.projectId) return { validPrompt, hasWarnings };
    const newErrors: string[][] = promptErrors.map(() => []);
    promptTexts.forEach((promptText, i) => {
      // empty check is for the first text field (prompt) only
      if (i === 0 && promptText.trim().length === 0) {
        newErrors.splice(i, 1, ['Please type a valid prompt.']);
        setPromptTexts(currentTexts =>
          produce(currentTexts, draft => {
            draft[i] = '';
          }),
        );

        validPrompt = false;
      }
      if (!validatorConfig?.similarPrompts?.enabled) return;
      const { threshold = 1 } = validatorConfig.similarPrompts;
      for (let j = i + 1; j < promptTexts.length; j++) {
        const similarity = compareTwoStrings(promptTexts[i].trim(), promptTexts[j].trim());
        if (similarity > threshold) {
          newErrors.splice(i, 1, ['Please type less similar prompts.']);
          validPrompt = false;
        }
      }
    });
    setPromptErrors(newErrors);

    await Promise.all(
      promptTexts.map(async (promptText, i) => {
        // i = 0 is always the prompt
        // i = 1 is the reference text (if it exists)
        const chatBoxText = chatbotContext.chatBoxTexts[i];
        const { errors, warnings } =
          i === 0
            ? await API.selfserve.validatePrompt({
                promptText,
                projectId: props.projectId!,
                chatBoxText,
                assignmentId: props.assignmentId,
                ...(promptTexts.length > 1 && { referenceText: promptTexts[1] }),
              })
            : await API.selfserve.validateReferenceText({
                promptText,
                projectId: props.projectId!,
                chatBoxText,
                assignmentId: props.assignmentId,
              });
        if (errors.length > 0) {
          validPrompt = false;
          newErrors.splice(i, 1, [...newErrors[i], ...errors]);
        } else {
          newErrors.splice(i, 1, newErrors[i]);
        }

        if (warnings.length > 0) {
          setPromptWarnings(warnings);
          hasWarnings = true;
        }
      }),
    );
    setPromptErrors(newErrors);

    return { validPrompt, hasWarnings };
  };

  const onEnter = async () => {
    setSubmitting(true);
    const ref = transcriptRef?.current;
    if (ref) setTimeout(() => (ref.scrollTop = ref.scrollHeight + 1000), 1000);

    try {
      if (!promptWithWarningOnly && !chatbotContext.skipPromptValidation) {
        if (promptTexts.every((promptText: string) => promptText === '')) {
          setSubmitting(false);
          return;
        }

        const { validPrompt, hasWarnings } = await validatePrompt();
        if (!validPrompt) {
          setSubmitting(false);
          return;
        }
        // if there's no errors, but there are warnings, the prompt is valid and can be submitted again
        if (hasWarnings) {
          setSubmitting(false);
          setPromptWithWarningOnly(promptTexts[0]);
          return;
        }
      }
      if (chatbotContext.isStreaming) {
        setSubmitting(false);
      }

      await chatbotContext.submitPrompts(promptTexts, props.attachment.content);
    } catch (_e) {
      setSubmissionError(
        'Error generating response. You may try again in 10 seconds. If you have already retried twice, please skip this task.',
      );
      setTimeout(() => {
        setSubmissionError(undefined);
        setSubmitting(false);
      }, 10000);
      return;
    }

    if (ref) setTimeout(() => (ref.scrollTop = ref.scrollHeight + 1000), 2500);
    if (!chatbotContext.rlhfParams?.isFair || isRangedMultiTurn) {
      setPromptTexts(Array(chatbotContext.chatBoxTexts.length).fill(''));
    }
    setSubmitting(false);
  };

  // Dynamic Import of Sapling
  useEffect(() => {
    import('@scale/frontend-lib/sapling-module')
      .then(mod => {
        setSapling(mod.default);
        return;
      })
      .catch(_ => {
        setSapling(null);
      });
  }, []);

  const mountSaplingOnEditor = useCallback(() => {
    const editor = document.querySelector(`textarea[name='promptInput']`) as HTMLElement;
    if (!editor || !Sapling) {
      return;
    }
    if (!editor.hasAttribute(CONTENTEDITABLE_ATTRIBUTE_NAME)) {
      // apply Sapling to prompt input for spelling/grammar check
      editor.setAttribute(CONTENTEDITABLE_ATTRIBUTE_NAME, 'true');
      editor.setAttribute(DATA_GRAMM_ATTRIBUTE_NAME, 'false');
      Sapling.observe(editor);
    }
  }, [Sapling]);

  const debouncedMountSapling = useMemo(
    () => debounce(mountSaplingOnEditor, MOUNT_SAPLING_AFTER_X_MS_NO_TYPING),
    [mountSaplingOnEditor],
  );

  const unmountSaplingFromEditor = useCallback(() => {
    const editor = document.querySelector(`textarea[name='promptInput']`) as HTMLElement;
    if (!editor || !Sapling) {
      return;
    }
    if (editor.hasAttribute(CONTENTEDITABLE_ATTRIBUTE_NAME)) {
      editor.removeAttribute(CONTENTEDITABLE_ATTRIBUTE_NAME);
      editor.removeAttribute(DATA_GRAMM_ATTRIBUTE_NAME);
      Sapling.unobserve(editor);
    }
  }, [Sapling]);

  useEffect(() => {
    async function execute() {
      if (props.projectId && Sapling) {
        const { enabled } = await API.selfserve.fetchSpellingAndGrammarEnabled({
          projectId: props.projectId,
        });
        setSpellingAndGrammarCheckEnabled(enabled);
      }
    }
    execute();
  }, [props.projectId, Sapling]);

  useEffect(() => {
    async function fetchDisablePasting() {
      if (props.projectId && Sapling) {
        const { enabled: disablePastingEnabled } = await API.selfserve.fetchDisablePastingEnabled({
          projectId: props.projectId,
        });
        setDisablePasting(disablePastingEnabled);
      }
    }
    fetchDisablePasting();
  }, [props.projectId, Sapling]);

  useEffect(() => {
    async function execute() {
      if (props.projectId) {
        const validatorConfig = await API.selfserve.fetchRlhfValidatorConfig({
          projectId: props.projectId,
        });
        setValidatorConfig(validatorConfig);
      }
    }
    execute();
  }, [props.projectId]);

  useEffect(() => {
    if (disablePasting || spellingAndGrammarCheckEnabled) {
      if (chatbotContext.isDone || submitting) {
        unmountSaplingFromEditor();
      } else if (!chatbotContext.isDone) {
        mountSaplingOnEditor();
      }
    }
  }, [
    disablePasting,
    spellingAndGrammarCheckEnabled,
    chatbotContext.isDone,
    submitting,
    mountSaplingOnEditor,
    unmountSaplingFromEditor,
  ]);

  useEffect(() => {
    if (!disablePasting) {
      return;
    }
    if (lastKeystrokeTimestamp) {
      const detectAutotyper = setTimeout(() => {
        const numKeystrokes = keystrokeIntervals.length;
        let autotyperDetected = false;
        for (let step = 0; step < numKeystrokes; step = step + AUTOTYPER_DETECT_STEP) {
          if (step + AUTOTYPER_DETECT_LENGTH > numKeystrokes) {
            break;
          }
          const subset = keystrokeIntervals.slice(step, step + AUTOTYPER_DETECT_LENGTH);

          const avgInterval = _.mean(subset) ?? 0.0;
          if (avgInterval > AUTOTYPER_DETECT_MAX_INTERVAL_MS) {
            continue;
          }
          if (avgInterval / 2 > (_.max(subset) ?? 0.0) - (_.min(subset) ?? 0.0)) {
            autotyperDetected = true;
            break;
          }
        }
        if (autotyperDetected) {
          setPromptTexts(promptTexts.map(() => ''));
          setPromptErrors(promptErrors.map(() => [DISABLE_PASTING_ERROR_TEXT]));
        }
        setLastKeystrokeTimestamp(undefined);
        setKeystrokeIntervals([]);
      }, 3 * 1000);
      return () => clearTimeout(detectAutotyper);
    }
  }, [
    disablePasting,
    keystrokeIntervals,
    lastKeystrokeTimestamp,
    promptErrors,
    promptTexts,
    setPromptTexts,
  ]);

  const onChange = useMemo(() => {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, i: number) => {
      if (promptWithWarningOnly && event.target.value !== promptWithWarningOnly) {
        setPromptWithWarningOnly(undefined);
      }

      unmountSaplingFromEditor();
      // only can disable pasting on the prompt field (i = 0)
      if (disablePasting && i === 0) {
        // Detecting autotyper
        const timestamp = event.nativeEvent.timeStamp;
        if (lastKeystrokeTimestamp) {
          setKeystrokeIntervals([...keystrokeIntervals, timestamp - lastKeystrokeTimestamp]);
        }
        setLastKeystrokeTimestamp(timestamp);
        setPromptTexts(currentTexts =>
          produce(currentTexts, draft => {
            draft[i] = '';
          }),
        );
      }
      setPromptTexts(currentTexts =>
        produce(currentTexts, draft => {
          draft[i] = event.target.value;
        }),
      );

      const numWords = event.target.value.split(' ').length;
      if (numWords > MIN_WORDS_TO_MOUNT_SAPLING) {
        debouncedMountSapling();
      }
    };
  }, [
    debouncedMountSapling,
    unmountSaplingFromEditor,
    chatbotContext.chatBoxTexts,
    disablePasting,
    keystrokeIntervals,
    lastKeystrokeTimestamp,
    promptTexts,
    setPromptTexts,
  ]);

  const onReset = async () => {
    if (!confirm('Reset the entire chat?')) return;

    if (props.setAnnotations) {
      props.setAnnotations({});
    }

    // Reset prompt (use prefillPrompt if it exists)
    setPromptTexts(basePromptTexts);
    chatbotContext.resetChat();
  };

  const onUndo = () => {
    // Restore previous prompt
    if (chatbotContext.history.interactions.length > 0) {
      const prompt =
        chatbotContext.history.interactions[chatbotContext.history.interactions.length - 1].prompt;
      setPromptTexts([prompt, '']);
    }
    if (props.setAnnotations && chatbotContext.resetLastInteraction()) {
      props.setAnnotations(chatbotContext.resetLastInteraction()!);
    }
    chatbotContext.handleUndo();
  };

  const promptWordsTip = useMemo(() => {
    if (!validatorConfig?.wordCount?.enabled) return '';
    const subtaskMetadata = props.subtaskMetadata?.metadataVariableValues;
    const [minResponseLength, maxResponseLength] = [
      subtaskMetadata?.[AllowedRapidAttachmentManagerMetadata.MinResponseLength],
      subtaskMetadata?.[AllowedRapidAttachmentManagerMetadata.MaxResponseLength],
    ];
    let { min = 1, max } = validatorConfig?.wordCount;
    if (minResponseLength) min = minResponseLength;
    if (maxResponseLength) max = maxResponseLength;
    return max
      ? `; Prompt should have between ${min} and ${max} words.`
      : `; Prompt should have at least ${min} words.`;
  }, [validatorConfig, props.subtaskMetadata]);

  const referenceWordsTip = useMemo(() => {
    if (!validatorConfig?.referenceTextWordCount?.enabled) return '';
    const { min = 1, max } = validatorConfig?.referenceTextWordCount;
    return max
      ? `; Text should have between ${min} and ${max} words.`
      : `; Text should have at least ${min} words.`;
  }, [validatorConfig]);

  const topic = props.attachment.content || 'Start the conversation!';

  const text = {
    isBot: false,
    isAttachment: true,
    text: topic,
  };

  const [rows, setRows] = React.useState([text]);

  const continueAvailable = React.useMemo(
    () => answeringForm && !chatbotContext.isDone && !submitting,
    [answeringForm, chatbotContext.isDone, submitting],
  );

  useEffect(() => {
    const interactions = chatbotContext.history.interactions;

    if (interactions.length === 0) {
      setRows([text]);
      return;
    }

    let selectedInteraction = chatbotContext.selectedInteraction;

    // show next interaction while its streaming
    if (chatbotContext.isStreaming) selectedInteraction += 1;

    const lastInteraction =
      chatbotContext.streamingInteraction ??
      interactions[Math.max(0, Math.min(interactions.length - 1, selectedInteraction))];

    const _rows: Array<RowProps> = [text];
    const showForm =
      !lastInteraction.chosenCompletion ||
      (chatbotContext.readOnly && !chatbotContext.isDone) ||
      chatbotContext.overrideViewFields;
    const end = interactions.length;
    let interactionIndex = 0;
    for (const interaction of interactions.slice(0, end)) {
      _rows.push({ isBot: false, text: interaction.prompt });

      if (showForm && selectedInteraction === interactionIndex) {
        const _options: string[] = [];
        for (const c of lastInteraction.completions) {
          _options.push(`${c.text.length ? c.text : '(response missing)'}`);
        }
        _options.push(`Rank`);
        _rows.push({ options: _options });
      }
      if (interaction?.displayedCompletion || interaction?.chosenCompletion) {
        const botText =
          interaction?.displayedCompletion?.text || interaction.chosenCompletion?.text;
        _rows.push({ isBot: true, text: botText });
      }
      interactionIndex += 1;
    }

    if (
      chatbotContext.isDone ||
      chatbotContext.overrideViewFields ||
      chatbotContext.onPerConvoField ||
      chatbotContext.readOnly
    ) {
      _rows.push({
        isBot: false,
        text: 'All done! Great work.',
      });
      if (chatbotContext.perConversationFields && chatbotContext.perConversationFields.length > 0) {
        _rows.push({
          isPerConvo: true,
          options: ['Answer per conversation questions'],
        });
      }
    }

    setRows(_rows as any);
  }, [
    chatbotContext.history,
    chatbotContext.isDone,
    chatbotContext.isStreaming,
    chatbotContext.readOnly,
    chatbotContext.selectedInteraction,
    chatbotContext.onPerConvoField,
    chatbotContext.streamingInteraction,
  ]);

  const getStatusForOption = (interactionIdx: number, optionIdx: number, isPerConvo?: boolean) => {
    if (
      (!chatbotContext.perResponseFields || chatbotContext.perResponseFields?.length === 0) &&
      chatbotContext.rlhfParams?.numberOfCompletions &&
      optionIdx < chatbotContext.rlhfParams!.numberOfCompletions
    ) {
      return <div></div>;
    }

    const inErroredItems = isPerConvo
      ? chatbotContext.perConversationFields.filter((p: any) =>
          chatbotContext.erroredItems.includes(p.field_id),
        ).length > 0
      : chatbotContext.erroredItems.includes(`${interactionIdx + 1}_${optionIdx + 1}`);
    return inErroredItems ? (
      <FontAwesomeIcon color="#E53935" icon={'times-circle'} />
    ) : isPerConvo ? (
      <div></div>
    ) : !chatbotContext.checkIfItemBlank(interactionIdx, optionIdx) ? (
      <FontAwesomeIcon color="#6A5DF9" icon={'check-circle'} />
    ) : (
      <FontAwesomeIcon color="#6A5DF9" icon={['far', 'circle']} />
    );
  };

  const rankingText = React.useMemo(() => {
    return chatbotContext.rlhfParams?.editBestCompletion
      ? chatbotContext.rlhfParams?.numberOfCompletions === 1
        ? 'Edit Response'
        : 'Edit/Rank Options'
      : chatbotContext.isMakingChanges && chatbotContext.useEditPrompt
      ? 'Edit Prompt'
      : chatbotContext.rlhfParams?.perTurnFieldIds
      ? 'Answer Questions'
      : 'Rank Options';
  }, [chatbotContext.rlhfParams, chatbotContext.isMakingChanges, chatbotContext.useEditPrompt]);

  const getOptionButton = (row: any, option: string, optionIdx: number, rowIdx: number) => {
    const interactionIdx = Math.floor((rowIdx - 2) / 2);
    return option === 'Rank' ? (
      (!chatbotContext.overrideViewFields ||
        chatbotContext.readOnly ||
        chatbotContext.isMakingChanges ||
        (chatbotContext.overrideViewFields && chatbotContext.rlhfParams?.editBestCompletion) || // can edit best completion
        (chatbotContext.isMakingChanges && chatbotContext.useEditPrompt)) && // can edit prompt, show 'edit prompt' section
        chatbotContext.hasItemsOnRankingEditPage && (
          <div
            className={cx(styles.rankOptionButton, {
              [styles.rankOptionButtonUnselected]: chatbotContext.selectedCompletion !== optionIdx,
            })}
            key={optionIdx}
            onClick={() => chatbotContext.setSelectedCompletion(optionIdx)}
          >
            <div className={styles.rankOptionText}>
              <FontAwesomeIcon icon={'signal'} />
              {rankingText}
            </div>
            <div>{getStatusForOption(interactionIdx, optionIdx)}</div>
          </div>
        )
    ) : row.isPerConvo ? (
      <div key={optionIdx} className={styles.optionButtonContainer}>
        <div className={styles.optionTextRow}>
          <span>Conversation</span>
          <span>{getStatusForOption(interactionIdx, optionIdx, true)}</span>
        </div>
        <div
          className={cx(styles.optionButton, {
            [styles.selectedOptionButton]: chatbotContext.onPerConvoField,
            [styles.unselectedOptionButton]: !chatbotContext.onPerConvoField,
          })}
          onClick={() => chatbotContext.gotoSelectedInteraction(Math.floor((rows.length - 3) / 2))}
        >
          {option}
        </div>
      </div>
    ) : (
      <div key={optionIdx} className={styles.optionButtonContainer}>
        <div className={styles.optionTextRow}>
          <span>
            Option {optionIdx + 1}
            <span className={styles.stopButton}>
              {chatbotContext.isStreaming &&
                chatbotContext.stopStreaming &&
                props.canStopStreaming &&
                option.length > 0 &&
                option !== '(response missing)' && (
                  <a onClick={() => chatbotContext.stopStreaming && chatbotContext.stopStreaming()}>
                    Stop
                  </a>
                )}
            </span>
          </span>
          <span>{getStatusForOption(interactionIdx, optionIdx)}</span>
        </div>
        <div
          className={cx(styles.optionButton, {
            [styles.selectedOptionButton]: chatbotContext.selectedCompletion === optionIdx,
            [styles.unselectedOptionButton]: chatbotContext.selectedCompletion !== optionIdx,
          })}
          onClick={() => chatbotContext.setSelectedCompletion(optionIdx)}
        >
          {option}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div
        className={cx(styles.chatbotTranscript, {
          [styles.chatbotTranscriptScroll]: chatbotContext.fieldsToShow.length === 0,
        })}
        ref={transcriptRef}
      >
        <div>
          {rows.map((row: RowProps, idx) =>
            row.options ? (
              <div key={idx}>
                {row.options.map((option, optionIdx) =>
                  getOptionButton(row, option, optionIdx, idx),
                )}
                {chatbotContext.isMakingChanges && (
                  <div className={styles.optionButtonContainer}>
                    <div>
                      <Button type="primary" onClick={chatbotContext.startBranchConversation}>
                        Restart Conversation From Here
                      </Button>
                    </div>
                  </div>
                )}
                {!chatbotContext.isMakingChanges &&
                  !chatbotContext.isStreaming &&
                  !chatbotContext.readOnly &&
                  (!chatbotContext.isDone ||
                    // if 1 interaction, isDone will be true
                    (chatbotContext.isDone &&
                      chatbotContext.rlhfParams?.numberOfInteractions === 1)) && (
                    <div className={styles.undoRestartButtons}>
                      <Button
                        className={styles.chatbotPromptButton}
                        onClick={onUndo}
                        disabled={chatbotContext.history.interactions.length === 0}
                      >
                        Undo
                      </Button>
                      {chatbotContext.rlhfParams?.numberOfInteractions !== 1 && (
                        <Button
                          className={styles.restartButton}
                          disabled={
                            chatbotContext.isStreaming || (!answeringForm && !isRangedMultiTurn)
                          }
                          type="primary"
                          onClick={onReset}
                        >
                          Restart
                        </Button>
                      )}
                    </div>
                  )}
              </div>
            ) : (
              <div
                key={idx}
                className={
                  row.isAttachment
                    ? styles.chatbotRowAttachment
                    : row.isBot
                    ? styles.chatbotRowBot
                    : styles.chatbotRowUser
                }
              >
                <div className={styles.chatbowText}>
                  {row.isBot && (
                    <div style={{ width: '29px', height: '29px', flexShrink: 0 }}>
                      <ChatbotIcon />
                    </div>
                  )}
                  {row.isAttachment || useMarkdownPreview ? (
                    <div style={{ width: '100%' }}>
                      <SanitizedMarkdown
                        text={row.text || ''}
                        addBackgroundToTransparentImages
                        disableFuzzyLinks
                      />
                    </div>
                  ) : (
                    <div style={{ whiteSpace: 'pre-line' }}>{row.text}</div>
                  )}
                </div>
                {!row.isBot &&
                  idx < rows.length - 2 &&
                  idx !== 0 &&
                  ((chatbotContext.isDone && !continueAvailable) ||
                    chatbotContext.readOnly ||
                    chatbotContext.isMakingChanges) && (
                    <div
                      className={styles.classCircle}
                      onClick={() => {
                        if (npovRewriteOpen) {
                          onUndo();
                        } else {
                          chatbotContext.gotoSelectedInteraction(Math.floor((idx - 1) / 2));
                        }
                      }}
                    >
                      {chatbotContext.erroredItems.filter((e: string) =>
                        e.startsWith(`${Math.floor((idx - 1) / 2) + 1}`),
                      ).length > 0 ? (
                        <FontAwesomeIcon color="#E53935" icon={'pencil'} />
                      ) : (
                        <FontAwesomeIcon className={styles.classCircle} icon={'pencil'} />
                      )}
                    </div>
                  )}
                {row.isAttachment && !npovRewriteOpen && (
                  <div className={styles.switchRow}>
                    <Switch
                      checked={useMarkdownPreview}
                      onChange={() => {
                        setUseMarkdownPreview(!useMarkdownPreview);
                      }}
                    />
                    Enable Markdown
                  </div>
                )}
              </div>
            ),
          )}
        </div>
        {submitting && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '20px',
              color: '#6A5DF9',
              marginBottom: '10px',
            }}
          >
            <CircularProgress color="inherit" />
          </div>
        )}
        <div style={{ float: 'left', clear: 'both' }} />
      </div>
      <div className={styles.chatbotPrompt}>
        {!(submitting || answeringForm || chatbotContext.isDone || chatbotContext.readOnly) && (
          <div className={styles.textInputContainer}>
            <div style={{ width: '100%' }}>
              {submissionError && (
                <Alert severity="error" style={{ marginBottom: 10 }}>
                  {submissionError}
                </Alert>
              )}
              {chatBoxTexts.map((text, i) => (
                <div style={{ marginTop: 4 }} key={i}>
                  {promptErrors[i].map((err, j) => (
                    <Alert
                      key={j}
                      severity="error"
                      style={{ marginBottom: 10 }}
                      onClose={() => {
                        const newErrors = [...promptErrors];
                        newErrors[i].splice(j, 1);
                        setPromptErrors(newErrors);
                      }}
                    >
                      {err}
                    </Alert>
                  ))}
                  {i === 0 &&
                    promptWarnings.map((warning, j) => (
                      <Alert
                        key={j}
                        severity="warning"
                        style={{ marginBottom: 10 }}
                        onClose={() => {
                          const newWarnings = [...promptWarnings];
                          newWarnings.splice(j, 1);
                          setPromptWarnings(newWarnings);
                        }}
                      >
                        {warning}
                      </Alert>
                    ))}
                  <TextInput
                    onPaste={event => {
                      if (disablePasting && i === 0) {
                        const newErrors = [...promptErrors];
                        newErrors.splice(i, 1, [DISABLE_PASTING_ERROR_TEXT]);
                        setPromptErrors(newErrors);
                        event.preventDefault();
                      }
                    }}
                    multiline={true}
                    maxRows={3}
                    name={'promptInput'}
                    fullWidth
                    disabled={submitting || answeringForm || chatbotContext.isDone}
                    value={promptTexts[i]}
                    label={
                      promptErrors[i]?.length > 0
                        ? 'Please fix prompt errors'
                        : answeringForm
                        ? 'Answer the questions to the right'
                        : text
                    }
                    size={'large'}
                    onChange={e => onChange(e, i)}
                    error={promptErrors[i]?.length > 0}
                    minHeight="100px"
                    helperText={`Word Count: ${_.words(promptTexts[i]).length}${
                      i === 0 ? promptWordsTip : referenceWordsTip
                    }`}
                    InputProps={{
                      classes: {
                        root: styles.textInputProps,
                        notchedOutline: styles.notchedOutline,
                      },
                    }}
                  />
                </div>
              ))}
            </div>

            <div className={styles.buttonColumn}>
              <Button
                className={styles.chatbotPromptButton}
                disabled={
                  promptTexts[0].length === 0 ||
                  submitting ||
                  answeringForm ||
                  chatbotContext.isDone
                }
                type="primary"
                onClick={onEnter}
              >
                Enter
              </Button>
              <Button
                className={styles.chatbotPromptButton}
                onClick={onUndo}
                disabled={chatbotContext.history.interactions.length === 0}
              >
                Undo
              </Button>
              <Button
                className={styles.chatbotPromptButton}
                disabled={chatbotContext.isStreaming || (!answeringForm && !isRangedMultiTurn)}
                type="primary"
                onClick={onReset}
              >
                Restart
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const useChatBotViewStyles = makeStyles(theme => ({
  chatbowText: {
    display: 'flex',
    flexDirection: 'row',
    gap: '24px',
    alignItems: 'center',
    width: '100%',
  },
  chatbotRowAttachment: {
    display: 'flex',
    flexDirection: 'column',
    padding: '24px 68px 24px 48px',
    gap: '24px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
  },
  chatbotRowBot: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '24px 68px 24px 48px',
    gap: '24px',
    backgroundColor: '#F3F4F6',
    border: '1px solid #E5E7EB',
  },
  chatbotRowUser: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '24px 68px 24px 48px',
    gap: '24px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
  },
  switchRow: {
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: '20px',
    fontWeight: 500,
    color: '#4B5563',
  },
  selectedOptionButton: {
    backgroundColor: '#F0EFFE',
    border: '1px solid #8F86FA',
  },
  unselectedOptionButton: {
    backgroundColor: '#F3F4F6',
    border: '1px solid #6B7280',
    color: '#374151',
  },
  optionButtonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    margin: '34px',
  },
  optionButton: {
    borderRadius: '10px',
    padding: '24px',
    gap: '24px',
    cursor: 'pointer',
    whiteSpace: 'pre-line',
  },
  optionTextRow: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    color: '#374151',
    fontWeight: 500,
  },
  rankOptionButton: {
    color: '#6A5DF9',
    fontWeight: 500,
    fontSize: '14px',
    marginLeft: '34px',
    marginRight: '34px',
    marginBottom: '34px',
    paddingTop: '25px',
    cursor: 'pointer',
    borderTop: '1px solid #DBDFE5',
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  rankOptionText: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
  },
  rankOptionButtonUnselected: {
    color: '#6B7280',
  },
  classCircle: {
    color: '#6B7280',
    width: '16px',
    height: '16px',
    cursor: 'pointer',
  },
  textInputProps: {
    '&:hover $notchedOutline': {
      borderColor: theme.colors.periwinkle100,
    },
    backgroundColor: '#FFFFFF',
  },
  notchedOutline: {},
  textInputContainer: {
    display: 'flex',
    width: '100%',
    padding: '10px',
    paddingLeft: '34px',
  },
  continueButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
  chatbotTranscript: {
    height: 'calc(100% - 250px)',
    marginBottom: 16,
  },
  chatbotPrompt: {
    width: '100%',
    display: 'flex',
  },
  chatbotPromptButton: {
    marginLeft: 8,
    marginTop: 7,
  },
  restartButton: {
    marginLeft: 8,
    marginTop: 7,
    marginRight: '36px',
  },
  stopButton: {
    marginLeft: 8,
  },
  chatbotTranscriptScroll: {
    overflowY: 'auto',
  },
  buttonColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  undoRestartButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
}));

export default ChatBotViewV2;
